<template>
  <FormSubmitPopup
    :title="$t(isAddForecast ? 'addForecast' : 'forecastDetail')"
    v-model="value"
    :rightMethod="submit"
    @popupClose="$emit('close')"
    :rightButtonDisabled="isEmpty"
    hasAlert
    :rightButtonLoading="isLoading"
    large
  >
    <template v-slot:input>
      <v-flex md7 v-if="isAddForecast">
        <v-layout row justify-space-between>
          <v-flex md3>
            <v-autocomplete
              :items="forecastWarehouses"
              v-model="forecast.curWarehouse"
              item-text="warehouseName"
              item-value="warehouseKey"
              return-object
              :label="$t('warehouse')"
            />
          </v-flex>
          <v-flex></v-flex>
          <v-flex md3>
            <v-autocomplete
              :items="(forecast.curWarehouse && forecast.curWarehouse.sites) || []"
              v-model="forecast.warehouseSite"
              :label="$t('warehouseSite')"
              item-text="siteName"
              item-value="key"
              :return-object="false"
            />
          </v-flex>
          <v-flex></v-flex>
          <v-flex md3>
            <v-autocomplete
              :items="[
                { text: $t('air'), value: 'air' },
                { text: $t('ocean'), value: 'ocean' },
                { text: $t('ground'), value: 'ground' },
              ]"
              v-model="forecast.shipType"
              item-text="text"
              item-value="value"
              :label="$t('shipType')"
            />
          </v-flex>
        </v-layout>
      </v-flex>
      <v-flex v-if="isAddForecast || forecast.status === 'ready'">
        <v-layout justify-space-between>
          <v-flex md11 >
            <v-layout>
              <v-flex >
                <v-btn color="primary" title="importFile" :loading="isUploading" :disabled="isUploadBtnDisabled">
                  <v-icon dark left>cloud_upload</v-icon>
                  {{ $t('upload') }}
                  <input type="file" class="hideInput" @change="uploadForecast" accept=".csv,.xls,.xlsx" />
                </v-btn>
              </v-flex>
              <v-flex mr-4>
                <v-btn color="primary" @click="isAddPackage = true">
                  <v-icon dark left>add</v-icon>
                  {{ $t('package') }}
                </v-btn>
              </v-flex>
              <v-flex mr-5 md2>
              </v-flex>
              <v-flex >
                <v-btn color="primary" @click.stop="downloadTemplate">
                  <v-icon dark left class="mx-2">cloud_download</v-icon>
                  {{ $t('template') }}
                </v-btn>
              </v-flex>
              <v-flex >
                <v-btn color="primary" :disabled="(forecast.pkgItems && forecast.pkgItems.length < 1)" @click.stop="checkTracking">
                  <v-icon dark left class="mx-2">signal_wifi_statusbar_connected_no_internet_4</v-icon>
                  {{ $t('checkTracking') }}
                </v-btn>
              </v-flex>
              <v-flex >
                <v-btn color="primary" :disabled="(forecast.pkgItems && forecast.pkgItems.length < 1)" @click.stop="checkUpc">
                  <v-icon dark left class="mx-2">signal_wifi_statusbar_connected_no_internet_4</v-icon>
                  {{ $t('checkUPC') }}
                </v-btn>
              </v-flex>
            </v-layout>
          </v-flex>
          <v-flex xs4 md2 right id="text-field">
            <v-text-field
              append-icon="filter_list"
              :label="$t('tracking')"
              single-line
              hide-details
              v-model="filter"
              clearable
            ></v-text-field>
          </v-flex>
        </v-layout>
      </v-flex>
      <v-data-table
        :headers="headers"
        :search="filter"
        item-key="tracking"
        :items="forecast.pkgItems || []"
        class="elevation-2 myDense"
        :rows-per-page-items="rowPerPage"
      >
        <template v-slot:items="props">
          <td class="text-xs-left">
            <v-layout>
              <v-flex :class="
                  isCheckTracking && !isTrackingNumberValid(props.item.tracking)
                    ? 'partial_highlight'
                    : ''
                ">
                {{ props.item.tracking }}
              </v-flex>
              <v-tooltip top v-if="props.item.isCheckPackage && isPackageManagementOrg((forecast.curWarehouse && forecast.curWarehouse.warehouseKey) || forecast.warehouseKey)">
                <template v-slot:activator="data">
                  <div v-on="data.on">
                    <v-icon>add_task</v-icon>
                    </div>
                </template>
                <span>{{ $t('needCheckPackage') }}</span>
              </v-tooltip>
            </v-layout>
          </td>
          <td class="text-xs-left">
            <v-flex v-for="(pkgItem, index) in props.item.upcs" :key="index">
              <v-flex :class="
                  isCheckUpc && !isUpcValid(pkgItem.upc)
                    ? 'partial_highlight'
                    : ''
                ">{{ pkgItem.upc }}</v-flex>
            </v-flex>
          </td>
          <td class="text-xs-left">
            <v-flex v-for="(pkgItem, index) in props.item.upcs" :key="pkgItem.upc + index">
              <v-flex
                :class="
                  forecast.status && forecast.status === 'partial' && pkgItem.forecastQty !== pkgItem.inboundQty
                    ? 'partial_highlight'
                    : ''
                "
              >
                {{
                  !isAddForecast
                    ? `${pkgItem.forecastQty || 0} / ${pkgItem.inboundQty || 0}`
                    : `${pkgItem.forecastQty || 0}`
                }}</v-flex
              >
            </v-flex>
          </td>
          <td class="text-xs-left">
            <v-flex v-for="(pkgItem, index) in props.item.upcs" :key="index">
              <div class="textOverflowHide">{{ pkgItem.productName || getProductName(pkgItem.upc) || ''}}</div>
            </v-flex>
          </td>
          <td class="text-xs-center">
            <v-flex>
              <v-btn
                flat
                color="primary"
                @click.stop="editPkg(props.item)"
                :disabled="props.item.upcs.reduce((qty, item) => qty + item.inboundQty,0) > 0"
                >{{ $t('edit') }}
              </v-btn>
              <v-btn
                flat
                color="primary"
                @click.stop="deletePkg(props.item)"
                :disabled="props.item.upcs.reduce((qty, item) => qty + item.inboundQty,0) > 0"
                >{{ $t('delete') }}
              </v-btn>
            </v-flex>
          </td>
        </template>
      </v-data-table>
      <v-layout mt-3>
        <v-textarea
          :label="$t('note')"
          outline
          :disabled="forecast.status && forecast.status !== 'ready'"
          v-model="forecast.note"
          rows="5"
          counter="200"
          class="thinBox"
        ></v-textarea>
      </v-layout>
      <FormSubmitPopup
        :title="$t('addPackage')"
        v-if="isAddPackage"
        v-model="isAddPackage"
        :rightMethod="savePackage"
        @popupClose="
          () => {
            isAddPackage = false
            isEditItem = false
            reset()
          }
        "
        :rightButtonDisabled="isAddPkgEmpty || isDuplicateUpc"
        hasAlert
        :rightButtonLoading="isLoading"
        medium
      >
        <template v-slot:input>
          <v-flex>
            <v-layout justify-space-between>
              <v-flex md4>
                <v-text-field
                  hide-details
                  :disabled="isEditItem"
                  :rules="[fieldIsRequired('trackingNumber')]"
                  :label="$t('trackingNumber')"
                  v-model.trim="pkg.tracking"
                ></v-text-field>
              </v-flex>
              <v-flex md2>
                <v-btn color="primary" @click="addUPC"> <v-icon dark left>add</v-icon>UPC </v-btn>
              </v-flex>
              <v-flex md3>
                <v-tooltip top v-if="isPackageManagementOrg((forecast.curWarehouse && forecast.curWarehouse.warehouseKey) || forecast.warehouseKey)">
                <template v-slot:activator="data">
                  <div v-on="data.on"><v-checkbox :label="$t('checkPackage')" v-model="pkg.isCheckPackage"></v-checkbox></div>
                </template>
                <span>{{ $t('isCheckPackage') }}</span>
              </v-tooltip>
              </v-flex>
              <v-flex md3 right>
                <v-text-field
                  append-icon="filter_list"
                  :label="$t('upc')"
                  single-line
                  hide-details
                  v-model="upcFilter"
                  clearable
                ></v-text-field>
              </v-flex>
            </v-layout>
          </v-flex>
          <v-flex v-for="(item, index) in pkg.upcs || []" :key="index">
            <v-layout justify-space-between v-if="!(upcFilter && item.upc !== upcFilter)">
              <v-flex md4 mr-5>
                <v-text-field
                  hide-details
                  :rules="[fieldIsRequired('upc')]"
                  :label="$t('upc')"
                  v-model.trim="item.upc"
                ></v-text-field>
              </v-flex>
              <v-flex md4 mr-4>
                <v-text-field
                  hide-details
                  :label="$t('productName')"
                  v-model.trim="item.productName"
                ></v-text-field>
              </v-flex>
              <v-flex md3 right>
                <v-layout>
                  <v-text-field
                    hide-details
                    :rules="[fieldIsNumber()]"
                    :label="$t('quantity')"
                    v-model.number="item.forecastQty"
                  ></v-text-field>
                  <v-icon color="red" style="cursor: 'pointer'" @click.stop="pkg.upcs.splice(index, 1)">cancel</v-icon>
                </v-layout>
              </v-flex>
            </v-layout>
          </v-flex>
        </template>
      </FormSubmitPopup>
    </template>
  </FormSubmitPopup>
</template>

<script>
import FormSubmitPopup from './FormSubmitPopup.vue'
import { checkRules, timeTools, readXlsxSheetData, warehouseTools, stringTools } from '@/utils/tools'
import BarcodeFinder from '../utils/barcode'

export default {
  components: {
    FormSubmitPopup
  },
  mixins: [checkRules, timeTools, warehouseTools, stringTools],
  data () {
    return {
      isUploading: false,
      uploadedFiles: [],
      isLoading: false,
      isAddPackage: false,
      pkg: {
        tracking: '',
        upcs: [{upc: '', productName: '', forecastQty: 1}],
        isCheckPackage: false
      },
      filter: '',
      upcFilter: '',
      isEditItem: false,
      isCheckTracking: false,
      isCheckUpc: false,
      oldTrackings: []
    }
  },
  mounted () {
    if (!this.isAddForecast && this.forecast.pkgItems?.length) {
      this.oldTrackings = this.forecast.pkgItems.map(pkg => pkg.tracking.toUpperCase())
    }
  },
  computed: {
    isDuplicateUpc () {
      if (Array.isArray(this.pkg.upcs)) {
        return this.pkg.upcs.some((item, index) => {
          if (index === 0) return false
          return this.pkg.upcs.slice(0, index).some(item2 => item2.upc === item.upc)
        })
      }
      return false
    },
    historyTrackings () {
      return this.$store.getters.tenantForecastTrackings || []
    },
    products () {
      return this.$store.getters.productsWithUpcChangeableFlag || []
    },
    headers () {
      return [
        { text: this.$t('tracking'), value: 'tracking', align: 'left', sortable: false },
        { text: `${this.$t('upc')} / sku`, align: 'left', sortable: false },
        { text: this.isAddForecast ? this.$t('forecastQuantity') : `${this.$t('forecastQuantity')} / ${this.$t('inboundQuantity')}`, align: 'left', sortable: false },
        { text: this.$t('productName'), align: 'left', sortable: false },
        { text: this.$t('actions'), align: 'center', sortable: false }
      ]
    },
    rowPerPage () {
      return [30, 50, {text: this.$t('all'), value: -1}]  
    },
    isAddPkgEmpty () {
      if (this.pkg.tracking === '' || this.pkg.upcs.some(item => item.upc === '')) return true
      return false
    },
    isUploadBtnDisabled () {
      return this.isUploading
    },
    isEmpty () {
      return JSON.stringify(this.forecast.curWarehouse) === '{}' || !(this.forecast.warehouseSite && this.forecast.shipType && this.forecast.pkgItems?.length)
    },
    tenantInfo () {
      return this.$store.getters.tenant || {}
    },
    forecastWarehouses () {
      const {featureInboundForecast = [], featureInboundForecastPkg = []} = this.tenantInfo
      const warehouseKeys = [...new Set([...featureInboundForecast, ...featureInboundForecastPkg])]
      return this.warehouses.filter(warehouse => warehouseKeys.includes(warehouse.warehouseKey)) || []
    },
    warehouses () { 
      return this.$store.getters.warehouses
    }
  },
  methods: {
    reset () {
      this.pkg = {
        tracking: '',
        upcs: [{upc: '', productName: '', forecastQty: 1}],
        isCheckPackage: false
      }
    },
    isPackageManagementOrg (warehouseKey) {
      return this.tenantInfo.featureInboundForecastPkg?.includes(warehouseKey)
    },
    handlerHistoryTrackings (pkgItems = []) {
      const historySet = new Set(this.historyTrackings)
      let dupTrackings = new Set()
      let newAddTrackings = new Set()
      const currentTrackings = new Set()
      pkgItems.forEach(pkg => {
        pkg.tracking = pkg.tracking.toUpperCase()
        if (!historySet.has(pkg.tracking) && !currentTrackings.has(pkg.tracking)) {
          newAddTrackings.add(pkg.tracking)
        } else {
          dupTrackings.add(pkg.tracking)
        }
        currentTrackings.add(pkg.tracking)
      })

      return {
        dupTrackings,
        newAddTrackings
      }
    },
    getProductName (upc) {
      return this.products.find(product => product.upc === upc || product.sku?.includes(upc))?.name
    },
    isUpcValid (upc) {
      return BarcodeFinder.isUpcValid(upc)
    },
    isTrackingNumberValid (tracking) {
      let _trk = this.trimNonwordChar(tracking) 
      return !!this.guessCarrier(_trk).length
    },
    checkTracking () {
      this.isCheckTracking = true
    },
    checkUpc () {
      this.isCheckUpc = true
    },
    editPkg (pkg) {
      this.isAddPackage = true
      this.isEditItem = true
      pkg = JSON.parse(JSON.stringify(pkg))
      this.pkg = pkg || 
      {
        tracking: '',
        upcs: [{upc: '', productName: '', forecastQty: 1}],
        isCheckPackage: false
      }
    },
    deletePkg (pkg) {
      if (!confirm(this.$t('confirmDeletePackage'))) return
      this.forecast.pkgItems = this.forecast.pkgItems.filter(item => item.tracking.toUpperCase() !== pkg.tracking.toUpperCase()) || []
    },
    savePackage () {
      if (JSON.stringify(this.pkg) !== '{}') {
        if (!Array.isArray(this.forecast.pkgItems)) this.forecast.pkgItems = []
        this.pkg = {...this.pkg,
          tracking: this.trimNonwordChar(this.pkg.tracking),
          upcs: this.pkg.upcs.map(item => {
            item.upc = this.trimNonwordChar(item.upc)
            return item
          })}
        this.forecast.isPackageManagement && (this.pkg.packageStatus = 'initial')
        let index = this.forecast.pkgItems.findIndex(item => this.trimNonwordChar(item.tracking.toUpperCase()) === this.pkg.tracking.toUpperCase())
        if (index === -1) {
          this.forecast.pkgItems.unshift(this.pkg)
        } else {
          if (!this.isEditItem && !confirm(this.$t('dupTrackingNumber'))) throw Error(this.$t('dupTrackingNumber'))
          this.$set(this.forecast.pkgItems, index, this.pkg)
        }
        this.reset()
      }
    },
    addUPC () {
      if (!this.pkg.upcs) this.pkg.upcs = []
      this.pkg.upcs.unshift({upc: '', productName: '', forecastQty: 1})
    },
    async submit () {
      this.isAddForecast ? await this.addForecast() : await this.updateForecast()
    },
    async addForecast () {
      const queueLength = 5000
      let { pkgItems = [], curWarehouse: { warehouseKey = '' }, warehouseSite = '', shipType = '', note = '' } = this.forecast
      let { dupTrackings = new Set(), newAddTrackings = new Set() } = this.handlerHistoryTrackings(pkgItems)
      if (dupTrackings.size) {
        alert(this.$t('forecastTrackingDupWarning', {trackings: [...dupTrackings].join(', ')}))
        throw Error(this.$t('forecastTrackingDupWarning', {trackings: [...dupTrackings].join(', ')}))
      } 
      let historyTrackings = [...this.historyTrackings, ...newAddTrackings]
      historyTrackings.splice(0, historyTrackings.length - queueLength)
      let packages = {}
      let keywords = new Set([this.$store.getters.activeOrganization, warehouseKey, warehouseSite])
      pkgItems.forEach(pkg => {
        let { tracking = '', upcs = [], isCheckPackage = false } = pkg
        upcs.forEach(item => {
          item.upc && keywords.add(item.upc.toUpperCase())
        })
        tracking = this.trimNonwordChar(tracking.toUpperCase())
        packages[tracking] = {
          upcs: upcs
        }
        if (this.isPackageManagementOrg(warehouseKey)) {
          packages[tracking].packageStatus = 'initial'
          packages[tracking].isCheckPackage = isCheckPackage
        }
        let cornetTracking = this.guessCarrier(tracking)[0]?.tracking?.[0]
        cornetTracking && keywords.add(cornetTracking)
        keywords.add(tracking)
      })
      keywords = [...keywords]
      let payload = {
        isNewForecast: true,
        forecast: {
          organizationKey: this.$store.getters.activeOrganization,
          packages,
          warehouseKey,
          warehouseSite,
          shipType,
          note,
          keywords,
          status: 'ready'
        },
        historyTrackings
      }
      if (this.isPackageManagementOrg(warehouseKey)) {
        payload.forecast.isPackageManagement = true
        payload.forecast.shipmentStatus = 'initial'
      }
      let res = await this.$store.dispatch('updateForecast', payload)
      this.$emit('addForecast', {...res, createTime: new Date(), lastModifiedTime: new Date(), isMockItem: true})
    },
    async updateForecast () {
      const queueLength = 5000
      let { pkgItems = [], keywords = [], note = '', _key = '', lastModifiedTime, status, isMockItem = false, isPackageManagement = false } = this.forecast
      let newAddItems = []
      let curOldTrackings = []
      pkgItems.forEach(pkg => {
        if (!this.oldTrackings.includes(pkg.tracking.toUpperCase())) {
          newAddItems.push(pkg)
        } else {
          curOldTrackings.push(pkg.tracking.toUpperCase())
        }
      })
      let deletedTrackings = this.oldTrackings.filter(trk => !curOldTrackings.includes(trk))

      let { dupTrackings = new Set(), newAddTrackings = new Set() } = this.handlerHistoryTrackings(newAddItems)

      if (dupTrackings.size) {
        alert(this.$t('forecastTrackingDupWarning', {trackings: [...dupTrackings].join(', ')}))
        throw Error(this.$t('forecastTrackingDupWarning', {trackings: [...dupTrackings].join(', ')}))
      }

      let historyTrackings = [...this.historyTrackings, ...newAddTrackings].filter(trk => !deletedTrackings.includes(trk))
      historyTrackings.splice(0, historyTrackings.length - queueLength)

      let packages = {}
      pkgItems.forEach(pkg => {
        let { tracking = '', upcs = [], ...rest } = pkg
        upcs.forEach(item => {
          item.upc && keywords.push(item.upc.toUpperCase())
        })
        tracking = this.trimNonwordChar(tracking.toUpperCase())
        !isPackageManagement && delete rest.isCheckPackage
        packages[tracking] = rest
        packages[tracking].upcs = upcs
        let cornetTracking = this.guessCarrier(tracking)[0]?.tracking?.[0]
        cornetTracking && keywords.push(cornetTracking)
        keywords.push(tracking)
      })
      keywords = keywords.filter(trk => !deletedTrackings.includes(trk))
      keywords = [...new Set(keywords)]
      let isPartial = (status && status !== 'ready') && Object.keys(packages).some(key => {
        return packages[key].upcs.some(
          pkg => !pkg.inboundQty || pkg.inboundQty !== pkg.forecastQty
        )
      })

      let payload = {
        lastModifiedTime,
        forecast: {
          packages,
          note,
          keywords,
          key: _key,
          status: status === 'ready' ? status : (isPartial ? 'partial' : 'done')
        },
        historyTrackings
      }
      isMockItem && (payload.isMockItem = isMockItem)
      let res = await this.$store.dispatch('updateForecast', payload)
      this.$emit('updateForecast', {...res, lastModifiedTime: lastModifiedTime})
    },
    downloadTemplate () {
      this.$store.dispatch('downloadFile', {
        fullPath: 'templates/forecast-template.xlsx',
        name: 'forecast-template.xlsx'
      })
    },
    async uploadForecast (e) {
      this.isUploading = true
      try {
        const files = e.target.files
        let rawData = await readXlsxSheetData(files)
        if (rawData.length) return this.processSheetData(rawData)
        else this.isUploading = false
      } catch (error) {
        this.isUploading = false
        throw error.message
      }
    },
    processSheetData (rawData = []) {
      let isForecastPkgWarehouse = this.isPackageManagementOrg(this.forecast.curWarehouse?.warehouseKey)
      let trackingField, upcField, quantityField, productNameField, checkPackageField
      trackingField = rawData[0]['tracking'] ? 'tracking' : (rawData[0]['TRACKING'] ? 'TRACKING' : (rawData[0]['Tracking'] ? 'Tracking' : ''))
      upcField = rawData[0]['upc'] ? 'upc' : (rawData[0]['UPC'] ? 'UPC' : '')
      productNameField = rawData[0]['Product name'] ? 'Product name' : (rawData[0]['Product Name'] ? 'Product Name' : (rawData[0]['product name'] ? 'product name' : ''))
      quantityField = rawData[0]['quantity'] ? 'quantity' : (rawData[0]['QUANTITY'] ? 'QUANTITY' : (rawData[0]['Quantity'] ? 'Quantity' : ''))
      checkPackageField = rawData[0]['Check Package'] !== undefined ? 'Check Package' : (rawData[0]['Check package'] !== undefined ? 'Check package' : (rawData[0]['check package'] !== undefined ? 'check package' : ''))
      try {
        const map = new Map()
        rawData.forEach((line) => {
          if (!line[trackingField] || !line[upcField] || !line[quantityField]) return
          let tracking = this.trimNonwordChar(line[trackingField].toString().toUpperCase())
          let forecastQty = parseInt(line[quantityField])
          let upc = this.trimNonwordChar(line[upcField].toString())
          let productName = (line[productNameField] || '').toString().trim()
          let isCheckPackageField = (line[checkPackageField] || '').toString().toLowerCase().trim() === 'true'
          if (!map.has(tracking)) {
            let value = {
              tracking, 
              upcs: [{upc, forecastQty, productName}]
            }
            isForecastPkgWarehouse && (value.isCheckPackage = isCheckPackageField)
            map.set(tracking, value)
          } else {
            const pkg = map.get(tracking)
            const targetItem = pkg.upcs.find(item => item.upc === upc)
            isForecastPkgWarehouse && isCheckPackageField && (pkg.isCheckPackage = isCheckPackageField)
            if (targetItem) {
              targetItem.forecastQty += forecastQty
            } else {
              pkg.upcs.push({upc, forecastQty, productName})
            }
            map.set(tracking, pkg)
          }
        })

        this.forecast.pkgItems = [...map.values(), ...this.forecast.pkgItems || []]
      } catch (e) {
        console.error(e.message)
      } finally {
        this.isUploading = false
      }
    }
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    isAddForecast: {
      type: Boolean,
      default: false
    },
    forecast: {
      type: Object,
      default: () => ({
        warehouseSite: '',
        shipType: '',
        curWarehouse: {},
        note: '',
        pkgItems: []
      })
    }
  }
}
</script>

<style scoped>
  .hideInput {
    position: absolute;
    top: 0;
    right: 0;
    min-width: 100%;
    min-height: 100%;
    text-align: right;
    filter: alpha(opacity=0);
    opacity: 0;
    outline: none;
    cursor: inherit;
    display: block;
  }
  #text-field .v-text-field{
    padding-top: 5px;
  }
</style>



// WEBPACK FOOTER //
// src/components/ForecastDetailPopup.vue